import globalContentDe from '@/assets/data/de/global-content.json'
import globalContentEn from '@/assets/data/en/global-content.json'
import globalContentEs from '@/assets/data/es/global-content.json'
import globalContentFr from '@/assets/data/fr/global-content.json'
import globalContentIt from '@/assets/data/it/global-content.json'
import useApi from '@/composables/api'
import usePushNotificationUser from '@/composables/pushNotificationUser'
import { useMainStore } from '@/store/main'
import { defineStore } from 'pinia'
import { captureException, captureMessage } from '@sentry/vue'

const isValidUserData = (data: unknown): boolean => {
  if (typeof data !== 'object' || data === null) {
    return false
  }

  const user = data as {
    id?: unknown
    role?: unknown
    permissions?: unknown
    language?: unknown
  }

  const hasValidId = typeof user.id === 'number'
  const hasValidRole = typeof user.role === 'string' && user.role.length > 0
  const hasValidPermissions = Array.isArray(user.permissions) && user.permissions.every((permission) => typeof permission === 'string')
  const hasValidLanguage = typeof user.language === 'undefined' || typeof user.language === 'string'

  return hasValidId && hasValidRole && hasValidPermissions && hasValidLanguage
}

export const useAuthStore = defineStore('authStore', {
  state: (): AuthStore => ({
    api: useApi(),
    authenticated: false,
    user: null,
    userLocale: 'en'
  }),
  getters: {
    hasCompanyName(state) {
      return state.user?.company_name
    },
    isImpersonating(state) {
      const platinumUsers = ['platinum_admin', 'platinum_engineer', 'platinum_office', 'platinum_super_admin']
      return state.user?.company_name && platinumUsers.includes(state.user?.role) ? true : false
    },
    companyName(state) {
      return state.user?.company_name ? state.user?.company_name : ''
    },
    hasRolePlatinumSuperAdmin(state) {
      return state.user?.role === 'platinum_super_admin'
    },
    hasRolePlatinumEngineer(state) {
      return state.user?.role === 'platinum_engineer'
    },
    hasRolePartnerCustomer(state) {
      return state.user?.role === 'partner_customer'
    }
  },
  actions: {
    async logout() {
      useMainStore().isFetching = true

      await this.api.requestApiCsrfToken()
      const response = await fetch(`${import.meta.env.VITE_LARAVEL_PATH}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: this.api.getApiHeaders()
      })

      useMainStore().isFetching = false

      if (!response.ok) {
        console.error(response)
      } else {
        this.clearAuthentication()
      }
    },
    setUser() {
      // Get authenticated and user from local storage
      const authenticated = localStorage.getItem('authenticated')
      const user = localStorage.getItem('user')

      if (authenticated === 'true' && user) {
        try {
          const parsedUser = JSON.parse(user)

          if (!isValidUserData(parsedUser)) {
            this.clearAuthentication()
            return
          }

          this.authenticated = true
          this.user = parsedUser
          this.userLocale = parsedUser.language ?? 'en'
        } catch {
          this.clearAuthentication()
        }
      } else {
        this.clearAuthentication()
      }
    },
    async me() {
      this.setUser()
      try {
        const response = await fetch(`${import.meta.env.VITE_LARAVEL_PATH}/api/v1/user`, {
          method: 'GET',
          credentials: 'include',
          headers: this.api.getApiHeaders()
        })

        useMainStore().navigatorOnline = true
        useMainStore().removeSystemAlerts({ name: 'browsing-offline' })

        if (response.ok) {
          const data = await response.json()
          const isValidUser = isValidUserData(data)

          // Validate user data structure
          if (!isValidUser) {
            captureException(new Error('Invalid user data structure'), {
              extra: {
                userId: typeof data === 'object' && data !== null ? data.id : null,
                hasRole: typeof data === 'object' && data !== null ? !!data.role : false,
                hasPermissions: typeof data === 'object' && data !== null ? Array.isArray(data.permissions) : false,
                hasValidLanguage: typeof data === 'object' && data !== null ? typeof data.language === 'undefined' || typeof data.language === 'string' : false
              },
              level: 'warning'
            })

            this.clearAuthentication()
            return false
          }

          localStorage.setItem('authenticated', 'true')
          localStorage.setItem('user', JSON.stringify(data))
          localStorage.setItem('authTimestamp', Date.now().toString())

          this.authenticated = true
          this.userLocale = data.language ?? 'en'
          this.user = data
          return true
        } else {
          let errorBody: any = {}
          if (typeof response.clone === 'function') {
            errorBody = await response
              .clone()
              .json()
              .catch(() => ({}))
          }

          // Capture the failed auth attempt with the actual error message
          captureMessage('Authentication API call failed', {
            level: 'warning',
            extra: {
              status: response.status,
              statusText: response.statusText,
              errorMessage: errorBody.message,
              userAgent: navigator.userAgent
            }
          })

          console.error('Authentication failed:', response.status)
          // Only clear authentication for auth-related status codes
          if (response.status === 401 || response.status === 403 || response.status === 419) {
            this.clearAuthentication()
          }
          try {
            await this.api.handleErrors(response)
          } catch (error) {
            captureException(error, {
              extra: {
                context: 'me() - handleErrors failed',
                status: response.status,
                statusText: response.statusText
              }
            })
          }
          return false
        }
      } catch (error) {
        // Capture network/parsing errors
        captureException(error, {
          extra: {
            context: 'me() - fetch error',
            authenticated: this.authenticated
          }
        })

        useMainStore().navigatorOnline = false
        let msg
        setTimeout(() => {
          if (this.userLocale === 'de') {
            msg = globalContentDe.browsing_offline
          } else if (this.userLocale === 'es') {
            msg = globalContentEs.browsing_offline
          } else if (this.userLocale === 'fr') {
            msg = globalContentFr.browsing_offline
          } else if (this.userLocale === 'it') {
            msg = globalContentIt.browsing_offline
          } else {
            msg = globalContentEn.browsing_offline
          }

          useMainStore().setSystemAlerts({
            type: 'warning',
            name: 'browsing-offline',
            message: [msg]
          })
        }, 0)

        this.api.checkOnlineStatus()
        return this.authenticated
      }
    },
    clearAuthentication() {
      if (this.user) {
        usePushNotificationUser().forgetPushUser()
      }

      localStorage.removeItem('authenticated')
      localStorage.removeItem('user')
      localStorage.removeItem('authTimestamp')
      this.authenticated = false
      this.user = null
    }
  }
})
